import { EssayNode } from '../../hooks/useEssayApi';

// Stable client id for new nodes.
export function newNode(text = ''): EssayNode {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `n_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return { id, text, children: [] };
}

/** Return a new tree with the text of node `id` replaced. */
export function setText(nodes: EssayNode[], id: string, text: string): EssayNode[] {
  return nodes.map((n) => {
    if (n.id === id) return { ...n, text };
    if (n.children.length) return { ...n, children: setText(n.children, id, text) };
    return n;
  });
}

/** Return a new tree with an empty child appended under node `parentId`. */
export function addChild(nodes: EssayNode[], parentId: string): EssayNode[] {
  return nodes.map((n) => {
    if (n.id === parentId) return { ...n, children: [...n.children, newNode()] };
    if (n.children.length) return { ...n, children: addChild(n.children, parentId) };
    return n;
  });
}

/** Return a new tree with node `id` (and its subtree) removed. */
export function removeNode(nodes: EssayNode[], id: string): EssayNode[] {
  return nodes
    .filter((n) => n.id !== id)
    .map((n) => (n.children.length ? { ...n, children: removeNode(n.children, id) } : n));
}

/** Return a new tree with `node` inserted right after node `id`, same level. */
export function insertAfter(nodes: EssayNode[], id: string, node: EssayNode): EssayNode[] {
  const i = nodes.findIndex((n) => n.id === id);
  if (i >= 0) return [...nodes.slice(0, i + 1), node, ...nodes.slice(i + 1)];
  return nodes.map((n) =>
    n.children.length ? { ...n, children: insertAfter(n.children, id, node) } : n,
  );
}

/**
 * Строка плоского представления дерева. Перетаскивание считает позицию по
 * линейному списку — вложенные drop-зоны так не работают, — поэтому дерево на
 * время рендера разворачивается в строки, а после перетаскивания собирается
 * обратно.
 */
export interface FlatNode {
  id: string;
  text: string;
  parentId: string | null;
  depth: number;
}

/** Обход в глубину: каждая строка знает своего родителя и уровень. */
export function flatten(nodes: EssayNode[], parentId: string | null = null, depth = 0): FlatNode[] {
  return (nodes || []).flatMap((n) => [
    { id: n.id, text: n.text, parentId, depth },
    ...flatten(n.children || [], n.id, depth + 1),
  ]);
}

/** Обратная сборка. Порядок строк задаёт порядок детей. */
export function buildTree(rows: FlatNode[]): EssayNode[] {
  const byId = new Map<string, EssayNode>();
  const roots: EssayNode[] = [];
  for (const row of rows) {
    const node: EssayNode = { id: row.id, text: row.text, children: [] };
    byId.set(row.id, node);
    const parent = row.parentId ? byId.get(row.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/** Узел вместе с поддеревом, или null. */
export function findNode(nodes: EssayNode[], id: string): EssayNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findNode(n.children || [], id);
    if (found) return found;
  }
  return null;
}

function insertChildAt(
  nodes: EssayNode[],
  parentId: string,
  child: EssayNode,
  index: number,
): EssayNode[] {
  return nodes.map((n) => {
    if (n.id === parentId) {
      const i = Math.max(0, Math.min(index, n.children.length));
      return { ...n, children: [...n.children.slice(0, i), child, ...n.children.slice(i)] };
    }
    return n.children.length
      ? { ...n, children: insertChildAt(n.children, parentId, child, index) }
      : n;
  });
}

/**
 * Переносит узел (с поддеревом) в `parentId` на позицию `index` среди его
 * детей; `parentId === null` — на верхний уровень. Индекс считается уже без
 * переносимого узла.
 *
 * Перенос в самого себя или в собственного потомка оставляет дерево нетронутым:
 * иначе узел вырезается вместе с целью вставки и пропадает целиком.
 */
export function moveNode(
  nodes: EssayNode[],
  activeId: string,
  parentId: string | null,
  index: number,
): EssayNode[] {
  const active = findNode(nodes, activeId);
  if (!active) return nodes;
  if (parentId === activeId) return nodes;
  if (parentId && findNode(active.children, parentId)) return nodes;

  const without = removeNode(nodes, activeId);
  if (parentId === null) {
    const i = Math.max(0, Math.min(index, without.length));
    return [...without.slice(0, i), active, ...without.slice(i)];
  }
  return insertChildAt(without, parentId, active, index);
}

export interface Projection {
  depth: number;
  parentId: string | null;
  index: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

function arrayMove<T>(list: T[], from: number, to: number): T[] {
  const next = list.slice();
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

/**
 * Куда встанет перетаскиваемый пункт: уровень задаёт горизонтальный сдвиг,
 * родителя и позицию — соседние строки.
 *
 * `rows` — плоский список без потомков переносимого узла (они едут вместе с
 * ним и в расчёте позиции не участвуют).
 *
 * Уровень зажат с двух сторон. Сверху: на один глубже строки над местом
 * вставки — иначе в нумерации появился бы пропущенный уровень. Плюс `maxDepth`,
 * дальше которого DOCX-шаблон не умеет нумеровать. Снизу: не мельче строки под
 * местом вставки, иначе она осиротеет.
 */
export function getProjection(
  rows: FlatNode[],
  activeId: string,
  overId: string,
  offsetX: number,
  indentWidth: number,
  maxDepth: number,
): Projection | null {
  const activeIndex = rows.findIndex((r) => r.id === activeId);
  const overIndex = rows.findIndex((r) => r.id === overId);
  // Строка могла исчезнуть из списка прямо во время перетаскивания: потомки
  // схватанного узла схлопываются, а dnd-kit ещё какое-то время отдаёт их id.
  // Без проверки позиция считалась бы по индексу -1 и пункт уезжал бы не туда.
  if (activeIndex < 0 || overIndex < 0) return null;
  const reordered = arrayMove(rows, activeIndex, overIndex);
  const above = reordered[overIndex - 1];
  const below = reordered[overIndex + 1];

  const depth = clamp(
    rows[activeIndex].depth + Math.round(offsetX / indentWidth),
    below ? below.depth : 0,
    Math.min(above ? above.depth + 1 : 0, maxDepth),
  );

  const parentId = (): string | null => {
    if (depth === 0 || !above) return null;
    if (depth === above.depth) return above.parentId;
    if (depth > above.depth) return above.id;
    // Строка над нами глубже — родителя ищем выше по списку, на нужном уровне.
    return (
      reordered
        .slice(0, overIndex)
        .reverse()
        .find((r) => r.depth === depth)?.parentId ?? null
    );
  };

  const parent = parentId();
  return {
    depth,
    parentId: parent,
    index: reordered.slice(0, overIndex).filter((r) => r.parentId === parent).length,
  };
}
